import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { systemApi } from '@/lib/api/system';
import { generateUUID } from '@comp-sys/shared';
import type { HomepageConfig, HomepageSettings, CarouselSlide } from '@comp-sys/shared';

const DEFAULT_SETTINGS: HomepageSettings = {
  carousel: { autoPlay: true, intervalSeconds: 5 },
  sections: { carousel: true, stats: true, events: true },
  hero: { title: '', subtitle: '' },
  stats: { showEvents: true, showParticipants: true, showSchools: true, showCertificates: false },
  events: { count: 4, showViewAll: true },
  enrollmentConsult: { enabled: false, contacts: [], instructionMarkdown: '', enrollmentInstructions: [] },
};

export function useHomepageConfig() {
  const { data, isLoading } = useQuery<HomepageConfig>({
    queryKey: ['homepage-config'],
    queryFn: systemApi.getHomepageConfig,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const settings = useMemo<HomepageSettings>(() => {
    const raw = data?.settings;
    const consult = { ...DEFAULT_SETTINGS.enrollmentConsult, ...raw?.enrollmentConsult };
    // 向后兼容：如果 enrollmentInstructions 为空但 instructionMarkdown 有内容，自动迁移
    if ((!consult.enrollmentInstructions || consult.enrollmentInstructions.length === 0) && consult.instructionMarkdown?.trim()) {
      consult.enrollmentInstructions = [
        {
          id: generateUUID(),
          title: '',
          markdown: consult.instructionMarkdown,
        },
      ];
    }
    return {
      ...DEFAULT_SETTINGS,
      ...raw,
      enrollmentConsult: consult,
    };
  }, [data?.settings]);

  return {
    slides: (data?.slides ?? []).filter((s) => s.isVisible).sort((a, b) => a.order - b.order),
    allSlides: data?.slides ?? [],
    settings,
    isLoading,
  };
}
